import type { InventoryTypeEnum } from '../inventory/InventoryTypeEnum'
import { Product } from '../product/Product'
import { Field, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { RelatedEntity } from '../base/RelatedEntity'
import { Order } from '../order/Order'
import { WorkOrder } from '../work-order/WorkOrder'
import type { InventoryBySerialNumberStatusEnum } from './InventoryBySerialNumberStatusEnum'
import { generateEntityId } from '../generateEntityId'

@ObjectType()
@InputType('InventoryBySerialNumberInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class InventoryBySerialNumber extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ibsn') })
  declare _id: string

  @Field(() => Product)
  @Prop({ type: () => RelatedEntity })
  product: ReturnType<() => Product>

  @Field(() => Order)
  @Prop({ type: RelatedEntity })
  order: ReturnType<() => Order>

  @Field(() => String)
  @Prop({ type: String })
  transactionType: InventoryTypeEnum

  /**
   * The transaction to reference where this change happens
   * When transaction like sales, purchase, adjustment, the value for this should be required
   */
  @Field(() => String)
  @Prop({ type: String, index: true })
  transactionRef: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  createdInTransactionType?: InventoryTypeEnum

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  createdInTransactionRef?: string

  /**
   * Stores the original transactionRef before deactivation
   * When a serial number is deactivated by a negative adjustment, we update transactionRef to the adjustment ID
   * This field preserves the original transactionRef so we can restore it during rollback
   */
  @Field(() => String)
  @Prop({ type: String })
  originalTransactionRef?: string

  /**
   * Serial number code - unique for Individual items, shared for Batch items
   * For Individual: each IBSN row has a unique code
   * For Batch: multiple IBSN rows share the same code (one per unit in the batch)
   */
  @Field(() => String)
  @Prop({ type: String, index: true })
  declare code: string

  @Field(() => String)
  @Prop({ type: String, index: true })
  batchCode: string

  @Field(() => String)
  @Prop({ type: String })
  declare status: InventoryBySerialNumberStatusEnum

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  consumedInWorkOrderRef?: string

  /**
   * Field resolver: consumedInWorkOrder
   * Returns the work order where this serial number was consumed (transactionType = BuildConsumption).
   */
  @Field(() => WorkOrder, { nullable: true })
  consumedInWorkOrder: ReturnType<() => WorkOrder | null>
}
